import React, { memo, useState, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Bar } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Legend,
  Tooltip,
  Title,
} from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { useToasts } from 'react-toast-notifications'
import { chartThreeMonthColors } from '../../../../constant/constant'
import Loader from '../../../../components/Loader'
import NoData from '../components/NoData'
import DashboardItemTitle from '../components/DashboardItemTitle'
import axiosInstance from '../../../../services/config/AxiosIntance'

ChartJS.register(CategoryScale, LinearScale, BarElement, Legend, Tooltip, Title)

function formatMonthYear(ym) {
  // ym is in 'YYYY-MM'
  const [year, month] = ym.split('-')
  const date = new Date(year, parseInt(month, 10) - 1)
  return date.toLocaleString('default', { month: 'long', year: 'numeric' })
}

const COUNTRY_KEY = 'country'
const CITY_KEY = 'city'

const CountryCityUsageReport = () => {
  const { addToast } = useToasts()
  const filters = useSelector((state) => state.dashboard.filters)
  const [loading, setLoading] = useState(false)
  const [chartData, setChartData] = useState({
    labels: [''],
    datasets: [],
  })

  useEffect(() => {
    setLoading(true)
    const queryParams = new URLSearchParams(filters).toString()
    axiosInstance
      .get(`/dashboard/applicants-by-location?${queryParams}`)
      .then((res) => {
        // 1. Get all unique months (raw)
        const monthsRaw = [
          ...new Set(res.data.result.map((item) => item.application_month)),
        ]

        // 2. Format months as "June 2025"
        const months = monthsRaw.map(formatMonthYear)

        // 3. Get all unique country-city pairs
        const countryCityList = [
          ...new Set(
            res.data.result.map(
              (item) => `${item[COUNTRY_KEY]}|||${item[CITY_KEY]}`
            )
          ),
        ]

        // 4. Build datasets: one per country-city, each with data for each month
        const datasets = countryCityList.map((countryCity, idx) => {
          const [country, city] = countryCity.split('|||')
          return {
            label: `${country} - ${city}`,
            data: monthsRaw.map((monthRaw) => {
              // use monthsRaw here!
              const found = res.data.result.find(
                (item) =>
                  item.application_month === monthRaw &&
                  item[COUNTRY_KEY] === country &&
                  item[CITY_KEY] === city
              )
              return found ? Number(found.applicant_count) : 0
            }),
            backgroundColor:
              chartThreeMonthColors[idx % chartThreeMonthColors.length],
            borderColor: 'black',
            borderWidth: 1,
            barPercentage: 1,
          }
        })

        setChartData({
          labels: months, // Only months on the X-axis
          datasets,
        })
      })
      .catch((err) => {
        addToast(
          err?.response?.message ||
          err?.response?.data?.message ||
          'Internal Server Error',
          {
            appearance: 'error',
            autoDismiss: true,
          }
        )
      })
      .finally(() => {
        setLoading(false)
      })
  }, [filters])

  const options = useMemo(
    () => ({
      type: 'bar',
      data: chartData,
      maintainAspectRatio: false,
      plugins: {
        title: {
          color: 'black',
          display: true,
          align: 'start',
          position: 'top',
          text: 'Applicant Location Report',
          font: {
            size: 24,
            family: "'Lato', 'sans-serif'",
            weight: 300,
          },
        },
        datalabels: {
          display: true,
          color: (ctx) => ctx.dataset.backgroundColor,
          anchor: 'end',
          align: 'end',
        },
      },
      scales: {
        x: {
          grid: { display: false },
          stacked: false, // Set to false for grouped bars
        },
        y: {
          grid: { display: false },
          ticks: { padding: 0.5 },
          stacked: false, // Set to false for grouped bars
        },
      },
      responsive: true,
    }),
    [chartData]
  )

  const component = loading ? (
    <Loader />
  ) : chartData.labels.length ? (
    <Bar
      data={chartData}
      options={options}
      plugins={[ChartDataLabels]}
      width={50}
      height={100}
    />
  ) : (
    <>
      <DashboardItemTitle title="Country/City Usage Report" />
      <NoData />
    </>
  )

  return component
}

export default memo(CountryCityUsageReport)
